import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LocationInventoryView = ({ locationId, products, categories, lowStockAlerts, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or table

  const filteredProducts = products?.filter(product => {
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.barcode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.alu?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product?.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStockStatus = (product) => {
    if (product?.stock_quantity === 0) return { label: 'Out of Stock', color: 'error' };
    if (product?.stock_quantity <= product?.low_stock_threshold) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by name, barcode, or ALU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              icon="Search"
            />
          </div>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e?.target?.value)}
            className="w-full md:w-48"
          >
            <option value="all">All Categories</option>
            {categories?.map(cat => (
              <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
            ))}
          </Select>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              iconName="Grid3x3"
            />
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              iconName="List"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>
      {/* Low Stock Summary */}
      {lowStockAlerts?.length > 0 && (
        <div className="card bg-warning/5 border-warning/20">
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            Low Stock Items ({lowStockAlerts?.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStockAlerts?.slice(0, 6)?.map(product => (
              <div key={product?.id} className="bg-card rounded-lg p-3 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{product?.name}</p>
                  <p className="text-xs text-muted-foreground">Stock: {product?.stock_quantity}</p>
                </div>
                <span className="px-2 py-1 rounded text-xs font-medium bg-warning/10 text-warning">
                  Low
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Products Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts?.map(product => {
            const status = getStockStatus(product);
            return (
              <div key={product?.id} className="card hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center">
                  {product?.image_url ? (
                    <img src={product?.image_url} alt={product?.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Icon name="Package" size={48} color="var(--color-muted-foreground)" />
                  )}
                </div>
                <h3 className="font-semibold text-foreground mb-1">{product?.name}</h3>
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{product?.description}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-primary">${product?.price?.toFixed(2)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium bg-${status?.color}/10 text-${status?.color}`}>
                    {status?.label}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Stock: {product?.stock_quantity}</span>
                  {product?.barcode && <span>#{product?.barcode}</span>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Product</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Category</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Barcode/ALU</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Price</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Stock</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts?.map(product => {
                const status = getStockStatus(product);
                return (
                  <tr key={product?.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                          {product?.image_url ? (
                            <img src={product?.image_url} alt={product?.name} className="w-full h-full object-cover rounded" />
                          ) : (
                            <Icon name="Package" size={20} color="var(--color-muted-foreground)" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{product?.name}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{product?.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {product?.category?.name || 'Uncategorized'}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {product?.barcode || product?.alu || '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-semibold text-foreground">
                      ${product?.price?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-foreground">
                      {product?.stock_quantity}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium bg-${status?.color}/10 text-${status?.color}`}>
                        {status?.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {filteredProducts?.length === 0 && (
        <div className="card text-center py-12">
          <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">No products found</p>
        </div>
      )}
    </div>
  );
};

export default LocationInventoryView;